import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AtSign, Instagram, Facebook } from "lucide-react";
import WaitlistDialog from "@/components/WaitlistDialog";
import MinisterCredential from "@/components/MinisterCredential";
import { GATEWAYS, LOGO_URL } from "@/lib/gateways";
import useSEO from "@/hooks/useSEO";

const TM = () => (
  <span className="tm" aria-hidden="true">
    TM
  </span>
);

const SOCIAL_LINKS = [
  {
    name: "Threads",
    href: "https://www.threads.com/@selahandstonelegacy",
    Icon: AtSign,
    testid: "footer-social-threads",
  },
  {
    name: "Instagram",
    href: "https://www.instagram.com/selahandstonelegacy",
    Icon: Instagram,
    testid: "footer-social-instagram",
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/SelahandStoneLegacyHoldings",
    Icon: Facebook,
    testid: "footer-social-facebook",
  },
];

const Landing = () => {
  useSEO({
    title: "Selah & Stone Legacy Holdings — Family Legacy, Credit Restoration, Forensic Research",
    description:
      "Selah and Stone Legacy Holdings restores the foundations of the modern family. Free credit restoration with Selah Slate, spiritual guidance with Selah Downloads, forensic due diligence with Selah and Stone Research, notary and loan signing with Omni Signing Services, and Selah and Salt Home.",
    path: "/",
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [interestId, setInterestId] = useState("general");
  const [ctaLabel, setCtaLabel] = useState("Join the waitlist");

  // Smooth-scroll to the gateway anchor when arriving via a hash link
  // (e.g. selahandstonelegacy.com/#selah-downloads).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash?.slice(1);
    if (!hash) return;
    // Wait a beat so the gateway grid is in the DOM before scrolling.
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(t);
  }, []);

  const openDialog = (interest, label) => {
    setInterestId(interest);
    setCtaLabel(label || "Join the waitlist");
    setDialogOpen(true);
  };

  return (
    <div className="paper-grain relative min-h-screen" data-testid="landing-page">
      <div className="relative z-10">
        {/* Top bar */}
        <div className="px-6 sm:px-10 pt-8 sm:pt-10">
          <div className="mx-auto max-w-6xl flex items-start justify-between">
            <div data-testid="topbar-brand" className="leading-tight">
              <div
                className="font-sans-body text-[10px] sm:text-[11px] tracking-[0.32em] uppercase"
                style={{ color: "var(--ink)" }}
              >
                Selah &nbsp;&middot;&nbsp; Stone
              </div>
              <div
                className="font-sans-body text-[9px] sm:text-[10px] tracking-[0.42em] uppercase mt-1"
                style={{ color: "var(--olive)" }}
              >
                Legacy Holdings
              </div>
            </div>
            <div
              className="hidden sm:block font-sans-body text-[10px] tracking-[0.32em] uppercase pt-1"
              style={{ color: "var(--stone-deep)" }}
            >
              Est. MMXXIV
            </div>
          </div>
        </div>

        {/* Hero */}
        <header
          className="px-6 sm:px-10 pt-16 sm:pt-24 pb-20 sm:pb-28"
          data-testid="hero-section"
        >
          <div className="mx-auto max-w-5xl text-center">
            <img
              src={LOGO_URL}
              alt="Selah and Stone Legacy Holdings seal"
              className="logo-blend mx-auto h-32 w-32 sm:h-40 sm:w-40 reveal"
              style={{ animationDelay: "60ms" }}
              data-testid="hero-logo"
            />

            <p
              className="mt-8 font-sans-body text-[10px] sm:text-[11px] tracking-[0.42em] uppercase reveal"
              style={{ color: "var(--olive)", animationDelay: "200ms" }}
              data-testid="hero-eyebrow"
            >
              One Family &nbsp;&middot;&nbsp; Breaking Curses &nbsp;&middot;&nbsp; Building Legacy
            </p>

            <h1
              className="font-serif-display font-light mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight reveal"
              style={{ color: "var(--ink)", animationDelay: "320ms" }}
              data-testid="hero-title"
            >
              Selah and Stone
              <span
                className="block italic font-normal mt-1"
                style={{ color: "var(--olive)" }}
              >
                Legacy Holdings<TM />
              </span>
            </h1>

            <div
              className="rule-ornament mt-8 mx-auto max-w-md reveal"
              style={{ animationDelay: "460ms" }}
            >
              <span className="font-serif-display italic text-sm">selah</span>
            </div>

            <p
              className="mt-6 font-serif-display italic text-xl sm:text-2xl reveal"
              style={{ color: "var(--stone-deep)", animationDelay: "560ms" }}
              data-testid="hero-tagline"
            >
              Restoring the Foundations of the Modern Family.
            </p>
          </div>
        </header>

        {/* Mission */}
        <section
          className="px-6 sm:px-10 pb-24 sm:pb-32"
          data-testid="mission-section"
        >
          <div className="mx-auto max-w-3xl text-center">
            <p
              className="font-sans-body text-[10px] tracking-[0.36em] uppercase"
              style={{ color: "var(--stone-deep)" }}
            >
              The Mission
            </p>
            <p
              className="mt-6 font-serif-display text-2xl sm:text-3xl lg:text-[34px] leading-[1.35] font-light"
              style={{ color: "var(--ink)" }}
              data-testid="mission-statement"
            >
              &ldquo;We help you build, protect, and restore the generational
              foundations of the home. From the due diligence of professional
              research and the careful witnessing of your most important
              documents, to the rebuilding of financial wholeness and the
              preservation of sacred family rhythms, we provide you with the
              tools so your family can live a life{" "}
              <span
                className="italic"
                style={{ color: "var(--olive)" }}
              >
                rooted in truth
              </span>{" "}
              and built for eternity.&rdquo;
            </p>
          </div>
        </section>

        {/* Gateways */}
        <section
          id="gateways"
          className="px-6 sm:px-10 pb-24 sm:pb-32"
          data-testid="gateways-section"
        >
          <div className="mx-auto max-w-6xl">
            <div className="mb-12 sm:mb-16 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <p
                  className="font-sans-body text-[10px] tracking-[0.36em] uppercase"
                  style={{ color: "var(--olive)" }}
                >
                  Five Gateways
                </p>
                <h2
                  className="mt-3 font-serif-display text-4xl sm:text-5xl font-light"
                  style={{ color: "var(--ink)" }}
                  data-testid="gateways-heading"
                >
                  A switchboard for the household.
                </h2>
              </div>
              <p
                className="font-sans-body text-sm max-w-sm"
                style={{ color: "var(--stone-deep)" }}
              >
                Each door represents a quiet shift we are making on behalf of
                the family. Step through the one that speaks to you.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {GATEWAYS.filter((g) => !g.hidden).map((g) => (
                <article
                  key={g.id}
                  id={g.id}
                  className="gateway-card p-7 sm:p-8 flex flex-col scroll-mt-24"
                  data-testid={`gateway-card-${g.id}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="gateway-number text-3xl">{g.number}</span>
                    {g.status === "live" ? (
                      <span
                        className="font-sans-body text-[9px] tracking-[0.28em] uppercase px-2.5 py-1 inline-flex items-center gap-1.5"
                        style={{
                          color: "var(--parchment)",
                          background: "var(--olive)",
                          border: "1px solid var(--olive)",
                        }}
                      >
                        <span
                          aria-hidden="true"
                          className="inline-block w-1.5 h-1.5 rounded-full"
                          style={{ background: "var(--parchment)" }}
                        />
                        Live
                      </span>
                    ) : g.status === "coming-soon" ? (
                      <div className="flex flex-col items-end gap-1.5">
                        <span
                          className="font-sans-body text-[10px] tracking-[0.32em] uppercase"
                          style={{ color: "var(--stone)" }}
                        >
                          Gateway
                        </span>
                        <span
                          className="font-sans-body text-[9px] tracking-[0.28em] uppercase px-2.5 py-1"
                          style={{
                            color: "var(--stone-deep)",
                            border: "1px solid rgba(176,133,94,0.55)",
                            background: "rgba(176,133,94,0.08)",
                          }}
                        >
                          Coming Soon
                        </span>
                      </div>
                    ) : (
                      <span
                        className="font-sans-body text-[10px] tracking-[0.32em] uppercase"
                        style={{ color: "var(--stone)" }}
                      >
                        Gateway
                      </span>
                    )}
                  </div>

                  <div className="gateway-line mt-6" />

                  <h3
                    className="mt-6 font-serif-display text-3xl sm:text-[34px] leading-tight font-medium"
                    style={{ color: "var(--ink)" }}
                  >
                    {g.title}<TM />
                  </h3>
                  <p
                    className="mt-1 font-serif-display italic text-lg"
                    style={{ color: "var(--olive)" }}
                  >
                    {g.subtitle}
                  </p>

                  <p
                    className="mt-5 font-sans-body text-[14px] leading-relaxed"
                    style={{ color: "var(--stone-deep)" }}
                  >
                    {g.description}
                  </p>

                  {g.playHref && (
                    <a
                      href={g.playHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-2 font-sans-body text-[11px] tracking-[0.18em] uppercase underline decoration-dotted underline-offset-4 hover:no-underline"
                      style={{ color: "var(--olive)" }}
                      data-testid={`gateway-play-${g.id}`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734c0-.351.219-.664.609-.92zm10.89 10.89l2.302 2.302-10.327 5.985 8.025-8.287zm3.196-3.196l2.866 1.66c.886.512.886 1.704 0 2.216l-2.96 1.717-2.5-2.5 2.594-3.093zM5.564 1.029l10.327 5.985-2.302 2.302L5.564 1.029z" />
                      </svg>
                      Also on Google Play
                    </a>
                  )}

                  {g.platforms && g.platforms.length > 0 && (
                    <div
                      className="mt-4 flex flex-wrap gap-2"
                      data-testid={`gateway-platforms-${g.id}`}
                    >
                      {g.platforms.map((p) =>
                        p.status === "coming-soon" ? (
                          <span
                            key={p.name}
                            className="inline-flex items-center gap-1.5 font-sans-body text-[10px] tracking-[0.18em] uppercase px-3 py-1.5"
                            style={{
                              color: "var(--stone-deep)",
                              border: "1px solid rgba(176,133,94,0.5)",
                              background: "rgba(176,133,94,0.06)",
                            }}
                          >
                            {p.name} · Soon
                          </span>
                        ) : (
                          <a
                            key={p.name}
                            href={p.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 font-sans-body text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 transition-colors"
                            style={{
                              color: "var(--parchment)",
                              background: "var(--olive)",
                              border: "1px solid var(--olive)",
                            }}
                          >
                            {p.name}
                            <span aria-hidden="true">&rarr;</span>
                          </a>
                        )
                      )}
                    </div>
                  )}

                  <div className="flex-1" />

                  {g.cta && (g.href ? (
                    <a
                      href={g.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost-olive mt-8 py-3 px-5 text-[11px] uppercase font-sans-body inline-flex items-center justify-center gap-2"
                      data-testid={`gateway-cta-${g.id}`}
                    >
                      {g.cta}
                      <span aria-hidden="true" className="text-xs">
                        &rarr;
                      </span>
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openDialog(g.id, g.cta)}
                      className="btn-ghost-olive mt-8 py-3 px-5 text-[11px] uppercase font-sans-body"
                      data-testid={`gateway-cta-${g.id}`}
                    >
                      {g.cta}
                    </button>
                  ))}
                </article>
              ))}
            </div>

            {/* Closing editorial card — centered below the grid */}
            <div className="mt-6 sm:mt-8 flex justify-center">
              <article
                className="p-7 sm:p-10 w-full max-w-md text-center relative overflow-hidden"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(63,84,104,0.06) 0%, rgba(176,133,94,0.08) 100%)",
                  border: "1px solid rgba(63,84,104,0.35)",
                }}
                data-testid="gateway-editorial-card"
              >
                <span
                  className="gateway-number text-3xl block"
                  style={{ color: "var(--olive)" }}
                >
                  VI
                </span>
                <div
                  className="gateway-line mt-6 mx-auto"
                  style={{ width: "2.5rem" }}
                />
                <h3
                  className="mt-6 font-serif-display text-3xl sm:text-[34px] leading-tight"
                  style={{ color: "var(--ink)" }}
                >
                  Not sure where to begin?
                </h3>
                <p
                  className="mt-4 font-sans-body text-[14px] leading-relaxed mx-auto max-w-sm"
                  style={{ color: "var(--stone-deep)" }}
                >
                  Send us a quiet note. We'll help you identify the right
                  gateway for your household.
                </p>
                <button
                  type="button"
                  onClick={() => openDialog("general", "Send Us a Note")}
                  className="btn-olive mt-8 py-3 px-8 text-[11px] uppercase font-sans-body"
                  data-testid="gateway-cta-general"
                >
                  Send Us a Note
                </button>
              </article>
            </div>
          </div>
        </section>

        {/* Book Teaser */}
        <section
          className="px-6 sm:px-10 pb-28 sm:pb-36"
          data-testid="book-section"
        >
          <div className="mx-auto max-w-6xl">
            <div
              className="relative p-8 sm:p-16 lg:p-20"
              style={{
                background: "rgba(255,255,255,0.4)",
                border: "1px solid rgba(176,133,94,0.45)",
              }}
            >
              <div className="grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7">
                  <p
                    className="font-sans-body text-[10px] tracking-[0.42em] uppercase"
                    style={{ color: "var(--olive)" }}
                  >
                    Forthcoming
                  </p>
                  <h2
                    className="mt-4 font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] font-light"
                    style={{ color: "var(--ink)" }}
                    data-testid="book-title"
                  >
                    The Selah
                    <span
                      className="italic"
                      style={{ color: "var(--olive)" }}
                    >
                      {" "}
                      Shift
                    </span>
                    <TM />
                  </h2>
                  <p
                    className="mt-6 font-serif-display italic text-xl sm:text-2xl"
                    style={{ color: "var(--stone-deep)" }}
                  >
                    The Work was done in the Selah.
                  </p>
                  <p
                    className="mt-5 font-sans-body text-[14px] max-w-xl leading-relaxed"
                    style={{ color: "var(--stone-deep)" }}
                  >
                    Documenting our journey from Holy Spirit-led guidance to
                    foundational restoration.
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      openDialog("selah-shift-book", "Sign Up for Early Access")
                    }
                    className="btn-olive mt-10 py-3 px-6 text-[11px] uppercase font-sans-body"
                    data-testid="book-cta"
                  >
                    Sign Up for Early Access
                  </button>
                </div>

                <div className="lg:col-span-5">
                  <div
                    className="relative mx-auto max-w-[280px] aspect-[3/4]"
                    style={{
                      background:
                        "linear-gradient(160deg, #ede0d5 0%, #d9c9bc 100%)",
                      border: "1px solid rgba(63,84,104,0.4)",
                      boxShadow:
                        "0 30px 60px -30px rgba(42,38,34,0.3), inset 0 0 0 8px rgba(245,232,224,0.6)",
                    }}
                  >
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                      <div
                        className="rule-ornament w-3/4"
                        style={{ color: "var(--stone)" }}
                      >
                        <span
                          className="font-serif-display italic text-xs"
                          style={{ color: "var(--stone-deep)" }}
                        >
                          selah
                        </span>
                      </div>
                      <h3
                        className="mt-6 font-serif-display text-3xl leading-tight font-medium"
                        style={{ color: "var(--ink)" }}
                      >
                        The Selah
                        <br />
                        <span
                          className="italic"
                          style={{ color: "var(--olive)" }}
                        >
                          Shift
                        </span>
                      </h3>
                      <span
                        className="mt-auto pt-10 font-sans-body text-[9px] tracking-[0.42em] uppercase text-center leading-relaxed"
                        style={{ color: "var(--stone-deep)" }}
                      >
                        Selah &amp; Stone
                        <br />
                        Legacy Holdings
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer
          className="px-6 sm:px-10 pb-14"
          data-testid="site-footer"
          style={{ borderTop: "1px solid rgba(176,133,94,0.35)" }}
        >
          <div className="mx-auto max-w-6xl pt-12">
            <div className="grid md:grid-cols-3 gap-10 md:gap-8">
              <div>
                <p
                  className="font-serif-display text-xl"
                  style={{ color: "var(--ink)" }}
                >
                  Selah and Stone
                  <span
                    className="italic block"
                    style={{ color: "var(--olive)" }}
                  >
                    Legacy Holdings<TM />
                  </span>
                </p>
                <p
                  className="mt-3 font-sans-body text-xs"
                  style={{ color: "var(--stone-deep)" }}
                >
                  &copy; {new Date().getFullYear()} Selah and Stone Legacy
                  Holdings, LLC. All rights reserved.
                </p>
                <p
                  className="mt-2 font-sans-body text-[11px] italic leading-relaxed"
                  style={{ color: "var(--stone-deep)" }}
                  data-testid="footer-tm-notice"
                >
                  Selah and Stone Legacy Holdings<sup className="tm">TM</sup>,
                  Selah Slate<sup className="tm">TM</sup>, Selah Downloads
                  <sup className="tm">TM</sup>, Selah and Stone Research
                  <sup className="tm">TM</sup>, Selah and Salt Home
                  <sup className="tm">TM</sup>, Family Legacy Audits
                  <sup className="tm">TM</sup>, Omni Signing Services
                  <sup className="tm">TM</sup>, and The Selah Shift
                  <sup className="tm">TM</sup> are trademarks of Selah and
                  Stone Legacy Holdings, LLC. All other marks are property
                  of their respective owners.
                </p>
                <p
                  className="mt-3 font-sans-body text-[11px] leading-relaxed"
                  style={{ color: "var(--stone-deep)" }}
                  data-testid="footer-affiliation"
                >
                  Selah and Stone Legacy Holdings is a U.S.-based family
                  legacy and forensic firm. We are not affiliated with any
                  textile or home goods companies outside of our family of
                  brands.
                </p>
              </div>

              <div className="text-left md:text-center">
                <p
                  className="font-sans-body text-[10px] tracking-[0.36em] uppercase"
                  style={{ color: "var(--olive)" }}
                >
                  Foundation
                </p>
                <p
                  className="mt-3 font-serif-display text-lg"
                  style={{ color: "var(--ink)" }}
                >
                  Selah and Stone Legacy Foundation
                </p>
                <p
                  className="mt-1 font-sans-body italic text-xs"
                  style={{ color: "var(--stone-deep)" }}
                >
                  501(c)(3) Coming Soon
                </p>

                <p
                  className="mt-6 font-sans-body text-[10px] tracking-[0.36em] uppercase"
                  style={{ color: "var(--olive)" }}
                >
                  Contact
                </p>
                <p className="mt-3 font-sans-body text-sm">
                  <a
                    href="mailto:admin@selahandstonelegacy.com"
                    className="underline hover:no-underline"
                    style={{ color: "var(--ink)" }}
                    data-testid="footer-contact-email"
                  >
                    admin@selahandstonelegacy.com
                  </a>
                </p>
                <p className="mt-4 font-sans-body text-xs flex flex-wrap items-center justify-start md:justify-center gap-x-3 gap-y-2">
                  <Link
                    to="/support"
                    className="underline hover:no-underline"
                    style={{ color: "var(--stone-deep)" }}
                    data-testid="footer-support-link"
                  >
                    Support
                  </Link>
                  <span style={{ color: "var(--stone)" }}>&middot;</span>
                  <Link
                    to="/privacy"
                    className="underline hover:no-underline"
                    style={{ color: "var(--stone-deep)" }}
                    data-testid="footer-privacy-link"
                  >
                    Privacy Policy
                  </Link>
                  <span style={{ color: "var(--stone)" }}>&middot;</span>
                  <Link
                    to="/terms"
                    className="underline hover:no-underline"
                    style={{ color: "var(--stone-deep)" }}
                    data-testid="footer-terms-link"
                  >
                    Terms &amp; Conditions
                  </Link>
                  <span style={{ color: "var(--stone)" }}>&middot;</span>
                  <Link
                    to="/trademarks"
                    className="underline hover:no-underline"
                    style={{ color: "var(--stone-deep)" }}
                    data-testid="footer-trademarks-link"
                  >
                    Trademarks
                  </Link>
                </p>
              </div>

              <div className="text-left md:text-right">
                <p
                  className="font-sans-body text-[10px] tracking-[0.36em] uppercase"
                  style={{ color: "var(--olive)" }}
                >
                  Disclosures
                </p>
                <p
                  className="mt-3 font-sans-body text-[11px] leading-relaxed"
                  style={{ color: "var(--stone-deep)" }}
                  data-testid="footer-disclosure-slate"
                >
                  <strong className="font-semibold">Selah Slate<TM /></strong> is a
                  financial wellness initiative. We are not a law firm or
                  credit repair clinic. Services are provided free of charge.
                </p>
                <p
                  className="mt-3 font-sans-body text-[11px] leading-relaxed"
                  style={{ color: "var(--stone-deep)" }}
                  data-testid="footer-disclosure-downloads"
                >
                  <strong className="font-semibold">Selah Downloads<TM /></strong>{" "}
                  provides spiritual guidance and is not a substitute for
                  professional counseling.
                </p>
                <p
                  className="mt-3 font-sans-body text-[11px] leading-relaxed"
                  style={{ color: "var(--stone-deep)" }}
                  data-testid="footer-disclosure-research"
                >
                  <strong className="font-semibold">
                    Selah and Stone Research<TM />
                  </strong>{" "}
                  provides background screening and open-source forensic due
                  diligence. We are not a licensed private investigative firm
                  and do not engage in surveillance, undercover work, or other
                  services reserved for licensed private investigators. All
                  services are performed in accordance with applicable law,
                  including the Fair Credit Reporting Act (FCRA) where
                  relevant.
                </p>
                <p
                  className="mt-3 font-sans-body text-[11px] leading-relaxed"
                  style={{ color: "var(--stone-deep)" }}
                  data-testid="footer-disclosure-omni"
                >
                  <strong className="font-semibold">
                    Omni Signing Services<TM />
                  </strong>{" "}
                  provides notary public and loan signing services. We are
                  not attorneys, do not practice law, and do not provide
                  legal, financial, tax, or mortgage advice. Notaries and
                  signing agents may not select forms, draft documents, or
                  explain the contents or legal implications of documents
                  signed. Any planned mortgage origination services will be
                  offered only through a properly licensed entity in
                  compliance with applicable federal and state law.
                </p>
              </div>
            </div>

            {/* Follow us — social row */}
            <div
              className="mt-14 flex flex-col items-center"
              data-testid="footer-social-row"
            >
              <p
                className="font-sans-body text-[10px] tracking-[0.36em] uppercase"
                style={{ color: "var(--olive)" }}
              >
                Follow
              </p>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
                {SOCIAL_LINKS.map(({ name, href, Icon, testid }) => (
                  <a
                    key={name}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2 transition-colors duration-300"
                    style={{ color: "var(--stone-deep)" }}
                    data-testid={testid}
                    aria-label={`Selah and Stone Legacy Holdings on ${name}`}
                  >
                    <Icon
                      className="w-[14px] h-[14px] transition-colors duration-300 group-hover:[color:var(--olive)]"
                      strokeWidth={1.4}
                      aria-hidden="true"
                    />
                    <span
                      className="font-sans-body text-[11px] tracking-[0.32em] uppercase transition-colors duration-300 group-hover:[color:var(--olive)]"
                    >
                      {name}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            <div className="rule-ornament mt-12">
              <span
                className="font-serif-display italic text-sm"
                style={{ color: "var(--stone-deep)" }}
              >
                selah
              </span>
            </div>

            <MinisterCredential />
          </div>
        </footer>
      </div>

      <WaitlistDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        interestId={interestId}
        ctaLabel={ctaLabel}
      />
    </div>
  );
};

export default Landing;
